/* eslint-disable react/no-danger -- when updating this component, consider replacing dangerous jsx properties */
import React, { useRef, useLayoutEffect, useState, useCallback } from 'react';

import { evalLegacyConnectInlineScripts } from '@confluence/connect-utils';
import { expVal } from '@confluence/feature-experiments';
import { ActivatableComponent } from '@confluence/loadable/entry-points/ActivatableComponent.ts';
import { ActivationTriggerType } from '@confluence/loadable';
import { isSpaceOverview } from '@confluence/named-routes/entry-points/routes-utils.ts';
import { useExtensionDimensions } from '@confluence/browser-metrics/entry-points/useExtensionDimensions.ts';
import { usePageState } from '@confluence/page-context';
import { ATL_GENERAL } from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';

type WebPanelProps = {
	html: string;
	/**
	 * Unique identifier for this web panel instance
	 */
	panelId?: string;
	panelLocation?: string;
};

export function WebPanel({ html, panelId, panelLocation }: WebPanelProps): React.JSX.Element {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const [{ contentId, spaceKey }] = usePageState();
	const dimensionsRef = useExtensionDimensions(
		panelLocation === ATL_GENERAL ? ATL_GENERAL : undefined,
		panelId,
		contentId || spaceKey || '',
	);
	const panelRef = useCallback(
		(element: HTMLDivElement | null) => {
			containerRef.current = element;
			dimensionsRef(element);
		},
		[dimensionsRef],
	);
	const [ssrSuccess, setSsrSuccess] = useState(true);
	const moduleLocationMatch = html.match(/"moduleLocation"\s*:\s*"atl\.general"/);

	// Restrict to just atl-general for now
	const isMatchForActivatable = Boolean(moduleLocationMatch) && isSpaceOverview();

	useLayoutEffect(() => {
		const container = containerRef.current;

		if (!container) {
			return;
		}

		if (
			isMatchForActivatable &&
			expVal('confluence_frontend_early_webpanel_initialization', 'isEnabled', false)
		) {
			if (window?.__HYDRATABLE__) {
				const scriptElement = container.querySelector('script.ap-iframe-body-script');
				if (scriptElement) {
					const scriptContent = scriptElement.textContent || '';
					const match = scriptContent.match(/"uniqueKey":"([^"]+)"/);
					if (match) {
						const uniqueKey = match[1];
						// Find iframe whose id contains the uniqueKey
						const iframe = container.querySelector(`iframe[id*="${uniqueKey}"]`);
						if (iframe) {
							return;
						}
					}
				}
			}

			if (ssrSuccess) {
				setSsrSuccess(false);
			}
		}
		void evalLegacyConnectInlineScripts(
			container,
			expVal('confluence_frontend_early_webpanel_initialization', 'isEnabled', false),
		);
	}, [html, panelId, ssrSuccess, isMatchForActivatable]);

	return isMatchForActivatable &&
		expVal('confluence_frontend_early_webpanel_initialization', 'isEnabled', false) ? (
		<div ref={panelRef}>
			<ActivatableComponent
				id={panelId ?? 'WebPanel'}
				triggers={[{ type: ActivationTriggerType.SSR_ONLY }]}
				ssrSuccess={ssrSuccess}
				analyticsName="WebPanel"
			>
				<div data-vc-ignore-if-no-layout-shift dangerouslySetInnerHTML={{ __html: html }} />
			</ActivatableComponent>
		</div>
	) : (
		<div
			ref={panelRef}
			data-vc-ignore-if-no-layout-shift
			dangerouslySetInnerHTML={{ __html: html }}
		/>
	);
}
